import { degreesToCompass } from "../lib/scorer";
import {
  UNIT_LABELS,
  compassToDeg,
  dirLevelColor,
  fmtSpeedRange,
  knotsColor,
} from "../lib/utils";
import type { CompassDir, ScoredDay, SpeedUnit } from "../lib/types";
import { SmallArrow } from "./Arrow";
import styles from "./DaySummary.module.css";

interface DaySummaryProps {
  day: ScoredDay;
  unit: SpeedUnit;
  /** Spot's ideal wind directions — drives the arrow accent colour. */
  idealDirs: readonly CompassDir[];
  /** Selected variant — range goes body-2 + foreground-strong. Used by
   *  SpotCard's forecast row when the day strip's selected day matches
   *  this column. */
  selected?: boolean;
}

/**
 * 72×72 day forecast block — wind speed range, coloured bar strip, and
 * direction arrow + label. Pure presentational; no click handling. Used
 * inside ForecastCol (where the parent button drives navigation) and as
 * the right-side compose target on search result list items.
 */
export function DaySummary({ day, unit, idealDirs, selected = false }: DaySummaryProps) {
  if (day.level === "skip") {
    return (
      <div className={styles.daySummary}>
        <span className={[styles.range, styles.rangeDim].join(" ")}>—</span>
        <span className={[styles.blobStrip, styles.blobStripHidden].join(" ")} />
        <span className={styles.dir}>—</span>
      </div>
    );
  }

  // For today specifically, prefer the live "now" hour's direction over
  // the whole-day average — once the morning has passed, the day's
  // representative dir is partly historical. The speed range stays
  // day-wide so the strip still shows the high/low. For other days the
  // current-hour fallback returns nothing and we use the day average.
  const nowHour = day.hours.find((h) => h.isNow);
  const displayDirDeg = nowHour?.dirDeg ?? day.dirDeg;
  const dir = degreesToCompass(displayDirDeg);
  const arrowDeg = compassToDeg(dir);
  const range = fmtSpeedRange(day.speedMinMs, day.speedMaxMs, unit);
  const maxKn = day.speedMaxMs * 1.944;
  const showBlob = maxKn >= 4;
  // Arrow tint reflects direction match against the spot's ideals —
  // independent of speed. (Knot strip beside the arrow already encodes
  // speed.)
  const dirAccent = dirLevelColor(displayDirDeg, idealDirs);

  return (
    <div
      className={[styles.daySummary, selected && styles.daySummarySelected]
        .filter(Boolean)
        .join(" ")}
    >
      <span className={styles.range}>
        {range}
        <span className={styles.rangeUnit}>{UNIT_LABELS[unit]}</span>
      </span>
      <span
        className={[styles.blobStrip, !showBlob && styles.blobStripHidden]
          .filter(Boolean)
          .join(" ")}
      >
        {day.bands.map((b, i) => {
          const kn = b.meanSpeedMs * 1.944;
          const color = kn < 3 ? "var(--wind-nil)" : knotsColor(kn);
          return (
            <span
              key={i}
              className={styles.blobSeg}
              style={{ background: color }}
            />
          );
        })}
      </span>
      <span className={styles.dir}>
        <span className={styles.dirArrow} style={{ color: dirAccent }}>
          <SmallArrow dirDeg={arrowDeg} size={12} />
        </span>
        {dir}
      </span>
    </div>
  );
}
